import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ;

function getLineupValue(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
}

function formatLineupNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return value;
  return numberValue.toLocaleString();
}

export default function MachineSupervisorLineupJobsView({
  supervisorId,
  allocatedMachines = [],
  onSnack,
  t,
  isDark,
}) {
  const [jobs, setJobs] = useState([]);
  const [loadingLineup, setLoadingLineup] = useState(false);
  const [filters, setFilters] = useState({
    machineCode: "",
    jobNumber: "",
    customer: "",
    priority: "",
  });

  const machineOptions = useMemo(() => {
    const unique = new Map();

    allocatedMachines.forEach((machine) => {
      const code = machine.MachineCode || machine.machineCode;
      if (!code || unique.has(code)) return;

      unique.set(code, {
        code,
        description: machine.MachineDescription || machine.description || "",
        groupCode: machine.MachineGroupCode || machine.groupCode || "",
      });
    });

    return Array.from(unique.values()).sort((a, b) =>
      a.code.localeCompare(b.code),
    );
  }, [allocatedMachines]);

  const fetchLineupJobs = useCallback(async () => {
    if (!supervisorId) return;

    setLoadingLineup(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/lineupjobs/${supervisorId}`,
      );

      const payload = res.data || {};
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.jobs)
          ? payload.jobs
          : [];

      setJobs(rows);
    } catch (err) {
      setJobs([]);
      onSnack?.(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load lineup jobs.",
        "error",
      );
    } finally {
      setLoadingLineup(false);
    }
  }, [onSnack, supervisorId]);

  useEffect(() => {
    fetchLineupJobs();
  }, [fetchLineupJobs]);

  const priorityOptions = useMemo(() => {
    const unique = new Set();

    jobs.forEach((job) => {
      if (job.WOpriority) unique.add(job.WOpriority);
    });

    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const machineCode = filters.machineCode.trim().toLowerCase();
    const jobNumber = filters.jobNumber.trim().toLowerCase();
    const customer = filters.customer.trim().toLowerCase();
    const priority = filters.priority.trim().toLowerCase();

    return jobs.filter((job) => {
      const jobMachine = String(job.machineCode || "").toLowerCase();
      const jobNo = String(job.jobNumber || "").toLowerCase();
      const jobCustomer = String(job.customer || "").toLowerCase();
      const jobPriority = String(job.WOpriority || "").toLowerCase();

      return (
        (!machineCode || jobMachine === machineCode) &&
        (!jobNumber || jobNo.includes(jobNumber)) &&
        (!customer || jobCustomer.includes(customer)) &&
        (!priority || jobPriority === priority)
      );
    });
  }, [filters, jobs]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const clearFilters = () => {
    setFilters({ machineCode: "", jobNumber: "", customer: "", priority: "" });
  };

  const totalPlannedQty = filteredJobs.reduce(
    (sum, job) => sum + Number(job.plannedQty || 0),
    0,
  );

  const totalSetupAndOperation = filteredJobs.reduce(
    (sum, job) => sum + Number(job.SetupAndOpeTime || 0),
    0,
  );

  return (
    <Fade in>
      <Box>
        <Paper
          sx={{
            borderRadius: "16px",
            p: { xs: 1.5, md: 2 },
            mb: 2,
            background: t.s1,
            border: `0.5px solid ${t.border}`,
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 850,
                  color: t.heading,
                  letterSpacing: "-0.02em",
                }}
              >
                Lineup Jobs
              </Typography>
              <Typography sx={{ fontSize: 12, color: t.muted, mt: 0.3 }}>
                View-only lineup records filtered to the supervisor’s allocated machines.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`${filteredJobs.length} shown`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                size="small"
                label={`Planned Qty: ${formatLineupNumber(totalPlannedQty)}`}
                variant="outlined"
                sx={{ fontWeight: 800, color: t.muted, borderColor: t.border }}
              />
              <Chip
                size="small"
                label={`Total Time: ${totalSetupAndOperation.toFixed(2)} hrs`}
                variant="outlined"
                sx={{ fontWeight: 800, color: t.muted, borderColor: t.border }}
              />
            </Stack>
          </Box>

          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Machine"
                value={filters.machineCode}
                onChange={handleFilterChange("machineCode")}
              >
                <MenuItem value="">All allocated machines</MenuItem>
                {machineOptions.map((machine) => (
                  <MenuItem key={machine.code} value={machine.code}>
                    {machine.code}
                    {machine.groupCode ? ` • ${machine.groupCode}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Job number"
                value={filters.jobNumber}
                onChange={handleFilterChange("jobNumber")}
                placeholder="Example: 25-10"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Customer"
                value={filters.customer}
                onChange={handleFilterChange("customer")}
                placeholder="Search customer"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Priority"
                value={filters.priority}
                onChange={handleFilterChange("priority")}
              >
                <MenuItem value="">All priorities</MenuItem>
                {priorityOptions.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={1}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <Tooltip title="Refresh lineup jobs" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={fetchLineupJobs}
                      disabled={loadingLineup}
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        border: `0.5px solid ${t.border}`,
                      }}
                    >
                      {loadingLineup ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 18, color: t.muted }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Clear filters" arrow>
                  <Box
                    component="button"
                    type="button"
                    onClick={clearFilters}
                    sx={{
                      minWidth: 62,
                      height: 38,
                      px: 1.25,
                      borderRadius: "10px",
                      border: `0.5px solid ${t.border}`,
                      background: t.s2,
                      color: t.muted,
                      fontWeight: 800,
                      fontSize: 11.5,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      "&:hover": { background: t.s3, color: t.heading },
                    }}
                  >
                    Clear
                  </Box>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          sx={{
            borderRadius: "14px",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            background: t.s1,
            border: `0.5px solid ${t.border}`,
            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 1550 }}>
            <TableHead>
              <TableRow sx={{ background: t.s2 }}>
                {[
                  "Machine",
                  "Seq",
                  "Job Number",
                  "WO Description",
                  "Customer",
                  "Priority",
                  "WO Qty",
                  "OP Qty",
                  "Planned Qty",
                  "Operation",
                  "Setup",
                  "Operational",
                  "Total",
                  "Unit",
                  "Start",
                  "End",
                  "Due Date",
                  "Delay",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      fontWeight: 850,
                      color: t.heading,
                      background: t.s2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loadingLineup ? (
                <TableRow>
                  <TableCell colSpan={18} align="center" sx={{ py: 5 }}>
                    <Stack spacing={1} alignItems="center">
                      <CircularProgress size={24} />
                      <Typography sx={{ fontSize: 12, color: t.muted, fontWeight: 700 }}>
                        Loading lineup jobs...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={18} align="center" sx={{ py: 5, color: t.muted }}>
                    No lineup jobs found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => (
                  <TableRow key={job.id || `${job.machineCode}-${job.jobSequenceNumber}-${job.jobNumber}`} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 850, color: t.heading }}>
                          {getLineupValue(job.machineCode)}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: t.muted }}>
                          {getLineupValue(job.MachineGroupCode, "")}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, color: t.primary }}>
                      {getLineupValue(job.jobSequenceNumber)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 850, color: t.heading, whiteSpace: "nowrap" }}>
                      {getLineupValue(job.jobNumber)}
                    </TableCell>
                    <TableCell sx={{ color: t.primary, minWidth: 220 }}>
                      {getLineupValue(job.WOdescription)}
                    </TableCell>
                    <TableCell sx={{ color: t.primary, minWidth: 210 }}>
                      {getLineupValue(job.customer)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getLineupValue(job.WOpriority)}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatLineupNumber(job.WOQty)}</TableCell>
                    <TableCell align="right">{formatLineupNumber(job.OPQty)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 850, color: t.heading }}>
                      {formatLineupNumber(job.plannedQty)}
                    </TableCell>
                    <TableCell sx={{ color: t.primary, minWidth: 260 }}>
                      {getLineupValue(job.OpDesctiption)}
                    </TableCell>
                    <TableCell align="right">{getLineupValue(job.SetupTime)}</TableCell>
                    <TableCell align="right">{getLineupValue(job.OperationalTime)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 850, color: t.heading }}>
                      {getLineupValue(job.SetupAndOpeTime)}
                    </TableCell>
                    <TableCell>{getLineupValue(job.TimeUnit)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{getLineupValue(job.opstartDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{getLineupValue(job.opendDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{getLineupValue(job.WOduedate)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: t.muted }}>
                      {getLineupValue(job.WOdelay)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Fade>
  );
}
